import { Component } from '@angular/core';
import { ProductService } from './services/product.service';
import { Product } from 'src/app/model/product';
import { ActivatedRoute, Router } from '@angular/router';
import { AppModeService } from 'src/app/services/app-mode.service';

@Component({
  selector: 'app-products',
  templateUrl: './products.component.html',
  styleUrls: ['./products.component.css'],
})
export class ProductsComponent {
  constructor(
    private readonly productService: ProductService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    public readonly appModeService: AppModeService,
  ) {}

  products: any[] = [];
  isMobileFilterOpen = false;

  selectedColors: string[] = [];
  selectedSizes: string[] = [];
  selectedSort = '';
  selectedCategory = '';

  colors: string[] = [];
  sizes: string[] = [];
  categories: Array<{ name: string; id?: string }> = [];

  readonly sortOptions = [
    { value: '', label: 'Default' },
    { value: 'NAME_ASC', label: 'Name A-Z' },
    { value: 'NAME_DESC', label: 'Name Z-A' },
    { value: 'PRICE_ASC', label: 'Price Low-High' },
    { value: 'PRICE_DESC', label: 'Price High-Low' },
  ];

  ngOnInit(): void {
    this.loadFilterOptions();

    this.route.queryParams.subscribe((params) => {
      const categoryParams = params['category'];
      const colorsParams = params['colors'];
      const sizesParams = params['sizes'];
      const sortParam = params['sort'] || '';

      const queryColors = Array.isArray(colorsParams)
        ? colorsParams
        : colorsParams
          ? [colorsParams]
          : [];
      const querySizes = Array.isArray(sizesParams)
        ? sizesParams
        : sizesParams
          ? [sizesParams]
          : [];

      const queryCategory = Array.isArray(categoryParams)
        ? categoryParams[0]
        : categoryParams || '';

      if (
        queryColors.length > 0 ||
        querySizes.length > 0 ||
        sortParam ||
        queryCategory
      ) {
        this.applyFilters({
          colors: queryColors,
          sizes: querySizes,
          sort: sortParam,
          category: queryCategory,
        });
        return;
      }

      this.productService.getProducts().subscribe((res: Product[]) => {
        this.products = this.mapResToProducts(res);
      });
    });
  }

  public ViewProduct(id: string) {
    this.router.navigate(['/products', id]);
  }

  trackByProduct(_: number, item: { id: string }): string {
    return item.id;
  }

  goBack(): void {
    const lang = localStorage.getItem('lang') || 'en';
    this.router.navigateByUrl(`/${lang}/home`);
  }

  mapResToProducts(res: Product[]) {
    return this.productService.mapToCardProducts(res, this.selectedColors);
  }

  openMobileFilter(): void {
    this.isMobileFilterOpen = true;
  }

  closeMobileFilter(): void {
    this.isMobileFilterOpen = false;
  }

  toggleColor(color: string): void {
    if (this.selectedColors.includes(color)) {
      this.selectedColors = this.selectedColors.filter(
        (item) => item !== color,
      );
      return;
    }
    this.selectedColors = [...this.selectedColors, color];
  }

  toggleSize(size: string): void {
    if (this.selectedSizes.includes(size)) {
      this.selectedSizes = this.selectedSizes.filter((item) => item !== size);
      return;
    }
    this.selectedSizes = [...this.selectedSizes, size];
  }

  applyMobileFilters(): void {
    this.applyFilters({
      colors: this.selectedColors,
      sizes: this.selectedSizes,
      sort: this.selectedSort,
      category: this.selectedCategory,
    });
    this.closeMobileFilter();
  }

  clearMobileFilters(): void {
    this.selectedColors = [];
    this.selectedSizes = [];
    this.selectedSort = '';
    this.selectedCategory = '';

    this.applyMobileFilters();
  }

  applyFilters(filters: any) {
    this.selectedColors = filters.colors || [];
    this.selectedSizes = filters.sizes || [];
    this.selectedSort = filters.sort || '';
    this.selectedCategory = filters.category || '';

    const cleanFilters = Object.fromEntries(
      Object.entries(filters).filter(([_, v]) => {
        if (Array.isArray(v)) {
          return v.length > 0;
        }
        return Boolean(v);
      }),
    );

    this.productService.filterProducts(cleanFilters).subscribe((res) => {
      this.products = this.mapResToProducts(res.data.filterProducts);
    });
  }

  /**
   * Load all filter options (colors, sizes, categories)
   * Uses ProductService which automatically handles app/web endpoint selection
   *
   * Note: Colors and sizes are only loaded for web mode
   * Mobile app view doesn't display these filters, so we skip loading them for performance
   */
  private loadFilterOptions(): void {
    this.productService.getAllSizes().subscribe((res) => {
      if (res) {
        this.sizes = this.sortSizes(res);
      }
    });

    // Load colors only for web mode

    this.productService.getAllColors().subscribe((res) => {
      if (res) {
        this.colors = res;
      }
    });

    // Load categories (used in both app and web modes)
    this.productService.getCategories().subscribe((res) => {
      if (res) {
        this.categories = res;
      }
    });
  }

  /**
   * Sort sizes in a logical order
   * Handles both standard sizes (XS, S, M, L, XL, XXL) and year-based sizes
   */
  private sortSizes(sizes: string[]): string[] {
    const normalizedSizes = (sizes || [])
      .map((size: string | null) => (size || '').trim())
      .filter((size: string) => Boolean(size));

    const sizeOrder = ['XS', 'S', 'M', 'L', 'XL', 'XXL', '3XL', '4XL'];

    const yearsSizes = normalizedSizes
      .filter((size: string) => size.toLowerCase().includes('year'))
      .sort((a: string, b: string) => {
        const numA = parseInt(a, 10);
        const numB = parseInt(b, 10);
        return isNaN(numA) || isNaN(numB) ? a.localeCompare(b) : numA - numB;
      });

    const regularSizes = normalizedSizes
      .filter((size: string) => !size.toLowerCase().includes('year'))
      .sort((a: string, b: string) => {
        const labelA = a.split('=')[0].trim().replace(/\s/g, '').toUpperCase();
        const labelB = b.split('=')[0].trim().replace(/\s/g, '').toUpperCase();
        const indexA = sizeOrder.indexOf(labelA);
        const indexB = sizeOrder.indexOf(labelB);

        if (indexA === -1 && indexB === -1) return labelA.localeCompare(labelB);
        if (indexA === -1) return 1;
        if (indexB === -1) return -1;

        return indexA - indexB;
      });

    return [...yearsSizes, ...regularSizes];
  }
}
